// .sdp encryption primitives — AES-GCM-256 with PBKDF2-SHA256-600k, via the Web
// Crypto API. Extracted from sdp_controller.js so the byte format is unit-testable
// (Node >= 20 exposes the same globalThis.crypto.subtle the browser does).
//
// On-disk byte layout (base64-encoded):
//     salt[16] | iv[12] | ciphertext(+GCM tag)
//
// This matches pcp_builder.html's CryptoUtils verbatim, so .sdp files round-trip
// between the legacy iframe editor and the native editor.

export const PBKDF2_ITER = 600000
export const SALT_LEN    = 16
export const IV_LEN      = 12

export async function deriveKey(password, salt) {
  const enc = new TextEncoder()
  const baseKey = await crypto.subtle.importKey(
    "raw", enc.encode(password), { name: "PBKDF2" }, false, [ "deriveKey" ]
  )
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: PBKDF2_ITER, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    [ "encrypt", "decrypt" ]
  )
}

export function bufToB64(buf) {
  const bytes = new Uint8Array(buf)
  let s = ""
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s)
}

export function b64ToBytes(b64) {
  const s = atob(b64)
  const a = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i)
  return a
}

export async function encryptPlan(plan, password) {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN))
  const iv   = crypto.getRandomValues(new Uint8Array(IV_LEN))
  const key  = await deriveKey(password, salt)
  const ct   = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv }, key, new TextEncoder().encode(JSON.stringify(plan))
  )
  const out = new Uint8Array(SALT_LEN + IV_LEN + ct.byteLength)
  out.set(salt, 0)
  out.set(iv, SALT_LEN)
  out.set(new Uint8Array(ct), SALT_LEN + IV_LEN)
  return bufToB64(out)
}

export async function decryptPlan(b64, password) {
  const raw  = b64ToBytes(b64)
  const salt = raw.slice(0, SALT_LEN)
  const iv   = raw.slice(SALT_LEN, SALT_LEN + IV_LEN)
  const ct   = raw.slice(SALT_LEN + IV_LEN)
  const key  = await deriveKey(password, salt)
  const pt   = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct)
  return JSON.parse(new TextDecoder().decode(pt))
}
